import React, { useState, useEffect, useMemo } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  MapPin,
  Clock,
  Truck,
  DollarSign,
  CheckCircle,
  XCircle,
  Phone,
  Zap,
  Award,
  Eye,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { collection, query, limit, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

interface Driver {
  id: string;
  name: string;
  phone: string;
  status: 'online' | 'offline' | 'busy' | 'suspended';
  vehicleType: 'car' | 'bike' | 'scooter';
  deliveries: number;
  cancellations: number;
  completionRate: number;
  totalEarnings: number;
  tips: number;
  activeOrderId: string | null;
  lastOnlineAt: Date | null;
  updatedAt: Date | null;
  location: { lat: number; lng: number } | null;
  suspended: boolean;
}

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
}

function mapDriverDoc(id: string, data: any): Driver {
  const deliveries = data.stats?.deliveries || 0;
  const cancellations = data.stats?.cancellations || 0;
  const totalJobs = deliveries + cancellations;
  return {
    id,
    name: data.displayName || data.name || 'Driver',
    phone: data.phone || '—',
    status: data.suspended ? 'suspended' : (data.status || 'offline'),
    vehicleType: data.vehicleType || 'car',
    deliveries,
    cancellations,
    completionRate: totalJobs > 0 ? Math.round((deliveries / totalJobs) * 1000) / 10 : 100,
    totalEarnings: (data.stats?.earningsCents || 0) / 100,
    tips: (data.stats?.tipsCents || 0) / 100,
    activeOrderId: data.activeOrderId || null,
    lastOnlineAt: toDate(data.lastOnlineAt),
    updatedAt: toDate(data.updatedAt),
    location: data.location?.lat != null ? { lat: data.location.lat, lng: data.location.lng } : null,
    suspended: data.suspended === true,
  };
}

const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  online: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  offline: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  busy: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  suspended: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

const vehicleIcons: Record<string, string> = {
  car: '🚗',
  bike: '🚴',
  scooter: '🛵',
};

interface DriverDetailPanelProps {
  driver: Driver;
  onClose: () => void;
}

function DriverDetailPanel({ driver, onClose }: DriverDetailPanelProps) {
  const statusStyle = statusConfig[driver.status];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setSuspended = async (suspended: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const call = httpsCallable(functions, 'adminSetDriverSuspended');
      await call({
        driverId: driver.id,
        suspended,
        reason: suspended ? 'Suspended from admin panel' : undefined,
      });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl backdrop-blur-sm">
                {vehicleIcons[driver.vehicleType]}
              </div>
              <div>
                <h2 className="text-xl font-semibold">{driver.name}</h2>
                <p className="text-white/80">{driver.phone}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <CheckCircle className="h-4 w-4 text-amber-300" />
                  <span className="font-medium">{driver.completionRate}%</span>
                  <span className="text-white/60">• {driver.deliveries} deliveries</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
            >
              <XCircle className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className={cn("px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-2", statusStyle.bg, statusStyle.text)}>
                <span className={cn("w-2 h-2 rounded-full", statusStyle.dot)} />
                <span className="capitalize">{driver.status}</span>
              </span>
              <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium flex items-center space-x-1">
                <span>{vehicleIcons[driver.vehicleType]}</span>
                <span className="capitalize">{driver.vehicleType}</span>
              </span>
            </div>
            {driver.activeOrderId && (
              <span className="text-sm text-body flex items-center space-x-1">
                <Truck className="h-4 w-4" />
                <span>On order #{driver.activeOrderId.slice(-6).toUpperCase()}</span>
              </span>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <Truck className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-heading">{driver.deliveries}</p>
                <p className="text-xs text-body">Deliveries</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <DollarSign className="h-6 w-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-heading">${driver.tips.toFixed(0)}</p>
                <p className="text-xs text-body">Tips Earned</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-heading">{driver.completionRate}%</p>
                <p className="text-xs text-body">Completion</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Award className="h-6 w-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-heading">${driver.totalEarnings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                <p className="text-xs text-body">Total Earned</p>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Driver Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-body flex items-center space-x-2">
                  <Phone className="h-4 w-4" />
                  <span>Phone</span>
                </span>
                <span className="text-heading font-medium">{driver.phone}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-50">
                <span className="text-body flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>Last Online</span>
                </span>
                <span className="text-heading font-medium">
                  {driver.lastOnlineAt ? driver.lastOnlineAt.toLocaleString() : 'Never'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-body flex items-center space-x-2">
                  <Zap className="h-4 w-4" />
                  <span>Last Update</span>
                </span>
                <span className="text-heading font-medium">
                  {driver.updatedAt ? driver.updatedAt.toLocaleString() : '—'}
                </span>
              </div>
              {driver.location && (
                <div className="flex items-center justify-between py-2 border-t border-gray-50">
                  <span className="text-body flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>Location</span>
                  </span>
                  <a
                    href={`https://www.google.com/maps?q=${driver.location.lat},${driver.location.lng}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary font-medium hover:underline"
                  >
                    {driver.location.lat.toFixed(4)}, {driver.location.lng.toFixed(4)}
                  </a>
                </div>
              )}
            </CardContent>
          </Card>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-xl flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <Button variant="outline" onClick={onClose} className="flex-1" disabled={busy}>
              Close
            </Button>
            {driver.suspended ? (
              <Button onClick={() => setSuspended(false)} loading={busy} className="flex-1 bg-green-500 hover:bg-green-600">
                <CheckCircle className="h-4 w-4 mr-2" />
                Reactivate
              </Button>
            ) : (
              <Button onClick={() => setSuspended(true)} loading={busy} className="flex-1 bg-red-500 hover:bg-red-600">
                <XCircle className="h-4 w-4 mr-2" />
                Suspend
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminDrivers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vehicleFilter, setVehicleFilter] = useState<string>('all');
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'drivers'), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDrivers(snap.docs.map((d) => mapDriverDoc(d.id, d.data())));
        setLoading(false);
      },
      (err) => {
        console.error('[AdminDrivers] subscription error:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.id === selectedDriverId) || null,
    [drivers, selectedDriverId]
  );

  const filteredDrivers = drivers.filter(driver => {
    const matchesSearch = 
      driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone.includes(searchTerm) ||
      driver.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || driver.status === statusFilter;
    const matchesVehicle = vehicleFilter === 'all' || driver.vehicleType === vehicleFilter;
    
    return matchesSearch && matchesStatus && matchesVehicle;
  });

  const onlineCount = drivers.filter(d => d.status === 'online').length;
  const busyCount = drivers.filter(d => d.status === 'busy').length;
  const totalDeliveries = drivers.reduce((sum, d) => sum + d.deliveries, 0);
  const totalEarnings = drivers.reduce((sum, d) => sum + d.totalEarnings, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Drivers</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Manage delivery drivers and fleet</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse" />
            <span>{onlineCount} online</span>
          </div>
          <div className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs sm:text-sm">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-amber-500 rounded-full" />
            <span>{busyCount} busy</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total Drivers', value: drivers.length, icon: Truck, color: 'bg-green-500' },
          { label: 'On Delivery', value: busyCount, icon: Zap, color: 'bg-purple-500' },
          { label: 'Total Deliveries', value: totalDeliveries.toLocaleString(), icon: CheckCircle, color: 'bg-amber-500' },
          { label: 'Driver Earnings', value: `$${totalEarnings.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, icon: DollarSign, color: 'bg-blue-500' },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-3 sm:p-4 flex items-center space-x-3 sm:space-x-4">
              <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white flex-shrink-0", stat.color)}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-body truncate">{stat.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-heading">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search drivers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 text-sm h-9 sm:h-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 h-9 sm:h-10 text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", showFilters && "rotate-180")} />
            </Button>
          </div>

          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Status</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'online', 'busy', 'offline', 'suspended'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize",
                        statusFilter === status
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Vehicle</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'car', 'bike', 'scooter'].map((vehicle) => (
                    <button
                      key={vehicle}
                      onClick={() => setVehicleFilter(vehicle)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize flex items-center space-x-1",
                        vehicleFilter === vehicle
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {vehicle !== 'all' && <span>{vehicleIcons[vehicle]}</span>}
                      <span>{vehicle}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drivers - Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {filteredDrivers.map((driver) => {
          const statusStyle = statusConfig[driver.status];

          return (
            <Card 
              key={driver.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedDriverId(driver.id)}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {driver.name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-heading text-sm truncate">{driver.name}</p>
                      <p className="text-xs text-body flex items-center space-x-1">
                        <span>{vehicleIcons[driver.vehicleType]}</span>
                        <span className="capitalize">{driver.vehicleType}</span>
                      </p>
                    </div>
                  </div>
                  <span className={cn(
                    "px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center space-x-1 flex-shrink-0",
                    statusStyle.bg, statusStyle.text
                  )}>
                    <span className={cn("w-1.5 h-1.5 rounded-full", statusStyle.dot)} />
                    <span className="capitalize">{driver.status}</span>
                  </span>
                </div>
                
                {driver.activeOrderId && (
                  <p className="text-xs text-body mb-2 flex items-center">
                    <Truck className="h-3 w-3 mr-1" />
                    On order #{driver.activeOrderId.slice(-6).toUpperCase()}
                  </p>
                )}
                
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-100 text-center">
                  <div>
                    <div className="flex items-center justify-center space-x-1">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      <span className="font-semibold text-heading text-sm">{driver.completionRate}%</span>
                    </div>
                    <p className="text-[10px] text-body">Completion</p>
                  </div>
                  <div>
                    <p className="font-semibold text-heading text-sm">{driver.deliveries.toLocaleString()}</p>
                    <p className="text-[10px] text-body">Deliveries</p>
                  </div>
                  <div>
                    <p className="font-semibold text-heading text-sm">${driver.totalEarnings.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                    <p className="text-[10px] text-body">Earnings</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Drivers - Desktop Table View */}
      <Card className="hidden lg:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Driver</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Completion</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Deliveries</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Earnings</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredDrivers.map((driver) => {
                  const statusStyle = statusConfig[driver.status];

                  return (
                    <tr 
                      key={driver.id} 
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedDriverId(driver.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold">
                            {driver.name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-heading text-sm">{driver.name}</p>
                            <p className="text-xs text-body flex items-center space-x-1">
                              <span>{vehicleIcons[driver.vehicleType]}</span>
                              <span className="capitalize">{driver.vehicleType}</span>
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-medium flex items-center space-x-1 w-fit",
                          statusStyle.bg, statusStyle.text
                        )}>
                          <span className={cn("w-1.5 h-1.5 rounded-full", statusStyle.dot)} />
                          <span className="capitalize">{driver.status}</span>
                        </span>
                        {driver.activeOrderId && (
                          <p className="text-xs text-body mt-1 flex items-center">
                            <Truck className="h-3 w-3 mr-1" />
                            #{driver.activeOrderId.slice(-6).toUpperCase()}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-1">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="font-medium text-heading">{driver.completionRate}%</span>
                        </div>
                        {driver.cancellations > 0 && (
                          <p className="text-xs text-body">{driver.cancellations} cancelled</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-heading text-sm">{driver.deliveries.toLocaleString()}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-heading text-sm">${driver.totalEarnings.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                        <p className="text-xs text-body">${driver.tips.toFixed(2)} tips</p>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDriverId(driver.id);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {filteredDrivers.length === 0 && (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <Truck className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-heading font-medium text-sm sm:text-base">No drivers found</p>
            <p className="text-body text-xs sm:text-sm mt-1">
              {drivers.length === 0
                ? 'Drivers appear here once they go online for the first time'
                : 'Try adjusting your search or filters'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Driver Detail Modal */}
      {selectedDriver && (
        <DriverDetailPanel 
          driver={selectedDriver} 
          onClose={() => setSelectedDriverId(null)}
        />
      )}
    </div>
  );
}
